<script setup lang="ts">
import { Loader2 as Loader2Icon } from '@lucide/vue'
import { computed, type Component } from 'vue'

/**
 * Detail-panel action button. Two layouts of the same action: `tile` (80px icon-over-label square,
 * the mobile quick actions) and `row` (icon + label inline, the desktop secondary actions).
 * `variant`: default (gray) · danger (red, e.g. Remove / Delete). `loading` swaps the icon for a
 * spinner. Single-root <button> so @click / :disabled / title fall through. Label via default slot.
 */
interface Props {
  icon: Component
  layout?: 'tile' | 'row'
  variant?: 'default' | 'danger'
  loading?: boolean
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  layout: 'tile',
  variant: 'default',
  loading: false,
  disabled: false,
})

const tile = computed(() => props.layout === 'tile')
const buttonClass = computed(() => {
  if (tile.value) {
    return props.variant === 'danger'
      ? 'flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg bg-red-50 text-red-600 transition-colors hover:cursor-pointer hover:bg-red-100 disabled:opacity-50'
      : 'flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg bg-gray-100 text-gray-600 transition-colors hover:cursor-pointer hover:bg-gray-200 hover:text-gray-900 disabled:opacity-50'
  }
  return props.variant === 'danger'
    ? 'flex w-full items-center gap-2.5 py-2 text-sm text-red-500 transition-colors hover:cursor-pointer hover:text-red-700 disabled:opacity-50'
    : 'flex w-full items-center gap-2.5 py-2 text-sm text-gray-900 transition-colors hover:cursor-pointer disabled:opacity-50'
})
const iconClass = computed(() => (tile.value ? 'h-6 w-6 shrink-0' : 'h-4 w-4 shrink-0'))
</script>

<template>
  <button type="button" :disabled="props.disabled || props.loading" :class="buttonClass">
    <Loader2Icon v-if="props.loading" :class="[iconClass, 'animate-spin']" />
    <component :is="props.icon" v-else :class="iconClass" />
    <span :class="tile ? 'text-center text-xs font-medium leading-tight' : ''"><slot /></span>
  </button>
</template>
